import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import ReactTooltip from "react-tooltip";
import { useSelector, useDispatch } from "react-redux";
import {
  setSubordinate,
  getTree,
  saveBulkReview,
  getActiveTab,
  LOADING_TREE
} from "store/actions/fileAction";
import { getSelectedClient, getSubordinates } from "store/actions/clientAction";
import debounce from "components/Forms/utils/debounce";
import Notification from "components/Notification";
import Menu from "components/Forms/Elements/Menu";
import AutoSuggest from "components/Forms/Elements/AutoSuggest";
import { isPending } from "helpers/file";
import Translate from "helpers/Translate";

function Search() {
  const dispatch = useDispatch();
  const [keyword, setKeyword] = useState("");
  const selectedClient = useSelector(state => state.client.selectedClient);
  const fileStatus = useSelector(state => state.file.getActiveTab);
  const subordinates = useSelector(state => state.client.subordinates);
  const selectedSubordinate = useSelector(state => state.file.getSubordinate);
  const multipleSelectedFiles = useSelector(
    state => state.file.multipleSelectedFiles
  );

  // Get the subordinates of the user by its client
  useEffect(() => {
    if (selectedClient?.id && !subordinates) {
      // Get the subordinates if they dont exists
      dispatch(getSubordinates(selectedClient.id));
    }
  }, [selectedClient, subordinates, dispatch]);

  // Fetch fileStatus
  useEffect(() => {
    if (!fileStatus) dispatch(getActiveTab());
  }, [fileStatus, dispatch]);

  // Fetch Client
  useEffect(() => {
    if (!selectedClient) dispatch(getSelectedClient());
  }, [selectedClient, dispatch]);

  const handleInput = useCallback(
    debounce(({ keyword, selectedClient, fileStatus, selectedSubordinate }) => {
      dispatch(
        getTree({
          client: selectedClient,
          fileStatus,
          searchQuery: keyword,
          subordinateId: selectedSubordinate?.id || ""
        })
      );
    }, 500),
    []
  );

  const handleMenuClick = item => {
    if (!multipleSelectedFiles || !multipleSelectedFiles.length) {
      Notification.warning(Translate({ id: "no_files_were_selected" }));
      return;
    }

    switch (item.id) {
      case "add_to_bulk_review":
        Notification.confirm(
          Translate({
            id: "add_files_to_bulk_review",
            values: [multipleSelectedFiles.length]
          }),
          {
            labelOk: Translate({ id: "add_to_bulk_review" }),
            onOk: () => {
              dispatch(
                saveBulkReview({
                  fileDocs: multipleSelectedFiles,
                  isDirectory: false,
                  flushOld: false
                })
              );
            }
          }
        );

        break;
      default:
        break;
    }
  };

  const buildMenuItems = () => {
    const menuItems = [];
    if (isPending(fileStatus)) {
      menuItems.push({
        id: "add_to_bulk_review",
        label: Translate({ id: "add_selected_files_to_bulk_review" }),
        iconClass: `fas fa-plus mr-5`
      });
    }
    return menuItems;
  };

  return (
    <div className="file-filter-box mb-10">
      <ReactTooltip />
      <i className="fas fa-search ml-5"></i>
      <input
        type="text"
        className="search form-input"
        placeholder={Translate({ id: "filter_file" })}
        value={keyword}
        onChange={e => setKeyword(e.target.value)}
        onKeyUp={e => {
          if (e.which === 27) {
            dispatch(
              getTree({
                client: selectedClient,
                fileStatus,
                subordinateId: selectedSubordinate?.id || ""
              })
            );
            setKeyword("");
          } else {
            dispatch({
              type: LOADING_TREE,
              payload: true
            });
            handleInput({
              keyword: e.target.value,
              fileStatus,
              selectedClient,
              selectedSubordinate
            });
          }
        }}
      />
      {/* Menu */}
      <ReactTooltip />
      <Menu
        isVisible={multipleSelectedFiles && multipleSelectedFiles.length}
        menuItems={buildMenuItems()}
        onMenuItemClick={handleMenuClick}
      >
        <i
          data-tip={Translate({ id: "actions_for_selected_files" })}
          className="fas fa-bolt power"
        ></i>
      </Menu>
      {/* Refresh */}
      <i
        className="fas fa-sync-alt refresh"
        data-tip={Translate({ id: "refresh_tree" })}
        onClick={e => {
          dispatch(
            getTree({
              client: selectedClient,
              fileStatus,
              searchQuery: keyword,
              subordinateId: selectedSubordinate?.id || ""
            })
          );
        }}
      ></i>
      {/* Select Subordinate Dropdown */}
      <AutoSuggest
        data={subordinates?.length > 0 ? subordinates : []}
        displayItemInList="fullname"
        displayItemInTextbox="fullname"
        scoppedValue={selectedClient}
        keyword={(keyword, selectedClient) => {
          dispatch(getSubordinates(selectedClient.id, keyword));
        }}
        onSelect={item => {
          dispatch(setSubordinate(item));
          dispatch(
            getTree({
              client: selectedClient,
              fileStatus,
              searchQuery: keyword,
              subordinateId: item?.id || ""
            })
          );
        }}
        placeholder={Translate({ id: "select_a_subordinate" })}
        onClose={() => {
          dispatch(getSubordinates(selectedClient.id));
        }}
        fieldValue={selectedSubordinate?.fullname}
      />
    </div>
  );
}

Search.propTypes = {
  getTree: PropTypes.func
};

export default Search;
